import { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import LanguageSelector from "../components/LanguageSelector";
import { getFirebaseErrorMessage } from "../utils/firebaseErrorHandler";

export default function ResetPassword() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    const ACCOUNT_ENUMERATION_CODES = new Set([
      "auth/user-not-found",
      "auth/user-disabled",
    ]);

    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      setSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      if (ACCOUNT_ENUMERATION_CODES.has(error?.code)) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setError(getFirebaseErrorMessage(error, t));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col justify-center bg-gray-50">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <div className="flex justify-center">
        <Logo />
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div>
          <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
            {t("auth.resetPassword.title")}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t("auth.resetPassword.description")}
          </p>
        </div>
        <div className="mt-8 bg-white p-10 shadow-lg sm:rounded-lg">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
          {success && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <div className="text-sm text-green-700">
                {t("auth.resetPassword.success")}
              </div>
            </div>
          )}
          <form className="space-y-6" onSubmit={handleResetPassword}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                {t("auth.resetPassword.emailLabel")}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs focus:border-indigo-500 focus:outline-hidden focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-xs hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                {isLoading
                  ? t("auth.resetPassword.sending")
                  : t("auth.resetPassword.button")}
              </button>
            </div>

            <div className="text-sm text-center">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                {t("auth.resetPassword.backToLogin")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
